/**
 * AttachmentUploadDialog Component
 *
 * Drag-and-drop file upload with tags and description.
 * Uses presigned URL flow for direct S3 upload.
 */

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Chip,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  useRequestUploadUrl,
  useRegisterAttachment,
  uploadFile,
} from '@/hooks/useAttachments';
import type { EntityRef } from '@/hooks/useAttachments';
import { useNotify } from '@/hooks/useNotify';

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/** An extra link the uploader can opt into with a checkbox (#3123). */
export interface OptionalUploadLink {
  link: EntityRef;
  /** `workorders` namespace key for the checkbox label. */
  labelKey: string;
  /** Optional `workorders` namespace key shown under the label. */
  hintKey?: string;
}

interface AttachmentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  entityLinks: EntityRef[];
  defaultTags?: string[];
  optionalLinks?: OptionalUploadLink[];
}

export function AttachmentUploadDialog({
  open,
  onClose,
  entityLinks,
  defaultTags,
  optionalLinks = [],
}: AttachmentUploadDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const requestUploadUrl = useRequestUploadUrl();
  const registerAttachment = useRegisterAttachment();

  const [files, setFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<string[]>(defaultTags ?? []);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [chosenOptional, setChosenOptional] = useState<Set<string>>(new Set());
  const linkKey = (link: EntityRef) => `${link.entityType}:${link.entityId}`;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReset = useCallback(() => {
    setFiles([]);
    setTags(defaultTags ?? []);
    setDescription('');
    setProgress(0);
    setUploading(false);
    setChosenOptional(new Set());
  }, [defaultTags]);

  const handleClose = useCallback(() => {
    if (!uploading) {
      handleReset();
      onClose();
    }
  }, [uploading, handleReset, onClose]);

  const handleFilesSelected = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const valid = Array.from(selectedFiles).filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    setFiles((prev) => [...prev, ...valid]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      handleFilesSelected(e.dataTransfer.files);
    },
    [handleFilesSelected]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file) continue;
        const fileProgress = (i / files.length) * 100;
        setProgress(fileProgress);

        await uploadFile(
          file,
          [...entityLinks, ...optionalLinks.filter((o) => chosenOptional.has(linkKey(o.link))).map((o) => o.link)],
          tags,
          description,
          (input) => requestUploadUrl.mutateAsync(input),
          (input) => registerAttachment.mutateAsync(input),
          (p) => setProgress(fileProgress + (p / files.length))
        );
      }

      setProgress(100);
      notify.success(t('attachments.uploadSuccess'));
      handleReset();
      onClose();
    } catch {
      notify.error(t('attachments.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('attachments.uploadFiles')}
    >
      <DialogTitle>{t('attachments.uploadFiles')}</DialogTitle>
      <DialogContent>
        {/* Drop zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: dragActive ? 'action.hover' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
            mb: 2,
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
          <Typography variant="body1">{t('attachments.dragOrClick')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('attachments.maxSize', { size: MAX_FILE_SIZE_MB })}
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => handleFilesSelected(e.target.files)}
        />

        {/* Selected files */}
        {files.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {files.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                label={`${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`}
                onDelete={() => removeFile(index)}
                size="small"
              />
            ))}
          </Box>
        )}

        {optionalLinks.length > 0 && (
          <FormGroup sx={{ mb: 2 }}>
            {optionalLinks.map((option) => {
              const key = linkKey(option.link);
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={chosenOptional.has(key)}
                      onChange={(e) => {
                        setChosenOptional((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(key); else next.delete(key);
                          return next;
                        });
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{t(option.labelKey)}</Typography>
                      {option.hintKey && (
                        <Typography variant="caption" color="text.secondary">{t(option.hintKey)}</Typography>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </FormGroup>
        )}

        {/* Tags */}
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          options={[]}
          value={tags}
          onChange={(_e, newValue) => setTags(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={option} size="small" {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('attachments.tags')}
              placeholder={t('attachments.addTag')}
              size="small"
              sx={{ mb: 2 }}
            />
          )}
        />

        {/* Description */}
        <TextField
          label={t('attachments.description')}
          placeholder={t('attachments.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          fullWidth
          size="small"
        />

        {/* Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('attachments.uploading')} {Math.round(progress)}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
        >
          {uploading ? t('attachments.uploading') : t('attachments.uploadFile')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
